import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const EMPTY_FORM = {
  name: "",
  specialties: "",
};

const extractErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof detail?.message === "string" && detail.message.trim()) {
    return detail.message.trim();
  }
  return fallbackMessage;
};

const normalizeSpecialties = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const validateStaffForm = (form) => {
  if (!form.name.trim()) {
    return "Il nome dello staff è obbligatorio.";
  }
  return null;
};

const createEditFormFromStaff = (member) => ({
  name: member?.name ?? "",
  specialties: Array.isArray(member?.specialties) ? member.specialties.join(", ") : "",
});

const buildStaffPatchPayload = (member, form) => {
  const payload = {};
  const normalizedName = form.name.trim();
  const normalizedSpecialties = normalizeSpecialties(form.specialties);
  const currentSpecialties = Array.isArray(member?.specialties) ? member.specialties : [];

  if (normalizedName !== (member?.name ?? "")) {
    payload.name = normalizedName;
  }
  if (JSON.stringify(normalizedSpecialties) !== JSON.stringify(currentSpecialties)) {
    payload.specialties = normalizedSpecialties;
  }

  return payload;
};

export function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingStaffId, setEditingStaffId] = useState("");
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [savingStaffId, setSavingStaffId] = useState("");
  const [togglingStaffId, setTogglingStaffId] = useState("");
  const [trashingStaffId, setTrashingStaffId] = useState("");

  const loadData = async () => {
    try {
      const response = await api.get("/staff");
      setStaff(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Caricamento staff non riuscito."));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const visibleStaff = useMemo(() => {
    return [...staff].sort((left, right) => {
      const leftActive = left.active !== false ? 1 : 0;
      const rightActive = right.active !== false ? 1 : 0;
      if (leftActive !== rightActive) {
        return rightActive - leftActive;
      }
      return (left.name ?? "").localeCompare(right.name ?? "", "it", {
        sensitivity: "base",
      });
    });
  }, [staff]);

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleEditChange = (event) => {
    setEditForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const submitStaff = async (event) => {
    event.preventDefault();
    const validationError = validateStaffForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmittingCreate(true);
    try {
      await api.post("/staff", {
        name: form.name.trim(),
        specialties: normalizeSpecialties(form.specialties),
      });
      toast.success("Membro staff aggiunto.");
      setForm(EMPTY_FORM);
      await loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile creare lo staff."));
    } finally {
      setSubmittingCreate(false);
    }
  };

  const startEdit = (member) => {
    setEditingStaffId(member.id);
    setEditForm(createEditFormFromStaff(member));
  };

  const cancelEdit = () => {
    setEditingStaffId("");
    setEditForm(EMPTY_FORM);
  };

  const submitEdit = async (member) => {
    const validationError = validateStaffForm(editForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = buildStaffPatchPayload(member, editForm);
    if (Object.keys(payload).length === 0) {
      toast.error("Nessuna modifica da salvare.");
      return;
    }

    setSavingStaffId(member.id);
    try {
      await api.patch(`/staff/${member.id}`, payload);
      toast.success("Staff aggiornato.");
      cancelEdit();
      await loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile aggiornare lo staff."));
    } finally {
      setSavingStaffId("");
    }
  };

  const toggleStaffActive = async (member) => {
    const nextActive = member.active === false;
    setTogglingStaffId(member.id);
    try {
      await api.patch(`/staff/${member.id}`, { active: nextActive });
      toast.success(nextActive ? "Staff riattivato." : "Staff disattivato.");
      if (editingStaffId === member.id) {
        cancelEdit();
      }
      await loadData();
    } catch (error) {
      toast.error(
        extractErrorMessage(
          error,
          nextActive ? "Impossibile riattivare lo staff." : "Impossibile disattivare lo staff."
        )
      );
    } finally {
      setTogglingStaffId("");
    }
  };

  const moveStaffToTrash = async (member) => {
    const confirmed = window.confirm(
      "Spostare questo membro staff nel cestino? Potrai ripristinarlo dalla pagina Cestino."
    );
    if (!confirmed) {
      return;
    }

    setTrashingStaffId(member.id);
    try {
      await api.post(`/staff/${member.id}/trash`);
      toast.success("Staff spostato nel cestino.");
      if (editingStaffId === member.id) {
        cancelEdit();
      }
      await loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile spostare lo staff nel cestino."));
    } finally {
      setTrashingStaffId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <PageHeader
        title="Staff"
        subtitle="Gestisci le persone che lavorano nella tua attività."
        titleTestId="staff-title"
      />
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="staff-form-card">
        <h2 className="text-lg font-semibold section-title" data-testid="staff-form-title">
          Aggiungi membro staff
        </h2>
        <form className="mt-4 grid md:grid-cols-2 gap-4" onSubmit={submitStaff} data-testid="staff-form">
          <input
            name="name"
            aria-label="Nome staff"
            value={form.name}
            onChange={handleChange}
            placeholder="Nome staff"
            className="border border-slate-200 rounded-sm h-10 px-3"
            data-testid="staff-name-input"
          />
          <input
            name="specialties"
            aria-label="Specialità staff"
            value={form.specialties}
            onChange={handleChange}
            placeholder="Specialità (separate da virgola)"
            className="border border-slate-200 rounded-sm h-10 px-3"
            data-testid="staff-specialties-input"
          />
          <button
            type="submit"
            disabled={submittingCreate}
            className="bg-slate-900 text-white rounded-sm h-10 px-4 font-medium transition-transform active:scale-95 disabled:opacity-60"
            data-testid="staff-submit-button"
          >
            {submittingCreate ? "Salvataggio..." : "Salva staff"}
          </button>
        </form>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="staff-list-card">
        <h2 className="text-lg font-semibold section-title" data-testid="staff-list-title">
          Elenco staff
        </h2>
        <div className="mt-4 space-y-3" data-testid="staff-list">
          {visibleStaff.map((member) => {
            const isEditing = editingStaffId === member.id;
            const isInactive = member.active === false;
            const isSaving = savingStaffId === member.id;
            const isToggling = togglingStaffId === member.id;
            const isTrashing = trashingStaffId === member.id;

            return (
              <div
                key={member.id}
                className={`border rounded-sm p-3 ${
                  isInactive ? "border-slate-300 bg-slate-50 text-slate-500" : "border-slate-200 bg-white"
                }`}
                data-testid={`staff-${member.id}`}
              >
                <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium" data-testid={`staff-name-${member.id}`}>
                        {member.name}
                      </p>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          isInactive ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"
                        }`}
                        data-testid={`staff-status-${member.id}`}
                      >
                        {isInactive ? "Disattivato" : "Attivo"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500" data-testid={`staff-specialties-${member.id}`}>
                      {member.specialties?.length ? member.specialties.join(", ") : "Nessuna specialità"}
                    </p>
                  </div>
                  {!isEditing && (
                    <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
                      <button
                        type="button"
                        onClick={() => startEdit(member)}
                        className="border border-slate-200 rounded-sm px-3 h-9 text-sm font-medium"
                        data-testid={`staff-edit-${member.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleStaffActive(member)}
                        disabled={isToggling}
                        className={`${isInactive ? "border border-slate-200" : "destructive-action"} rounded-sm px-3 h-9 text-sm font-medium disabled:opacity-60`}
                        data-testid={`staff-toggle-active-${member.id}`}
                      >
                        {isToggling ? "Aggiornamento..." : isInactive ? "Riattiva" : "Disattiva"}
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStaffToTrash(member)}
                        disabled={isTrashing}
                        className="destructive-action rounded-sm px-3 h-9 text-sm font-medium disabled:opacity-60"
                        data-testid={`staff-trash-${member.id}`}
                      >
                        {isTrashing ? "Spostamento..." : "Elimina"}
                      </button>
                    </div>
                  )}
                </div>

                {isEditing && (
                  <form
                    className="mt-4 grid gap-3 md:grid-cols-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      submitEdit(member);
                    }}
                    data-testid={`staff-edit-form-${member.id}`}
                  >
                    <input
                      name="name"
                      value={editForm.name}
                      onChange={handleEditChange}
                      placeholder="Nome staff"
                      className="border border-slate-200 rounded-sm h-10 px-3"
                    />
                    <input
                      name="specialties"
                      value={editForm.specialties}
                      onChange={handleEditChange}
                      placeholder="Specialità (separate da virgola)"
                      className="border border-slate-200 rounded-sm h-10 px-3"
                    />
                    <div className="flex flex-wrap gap-2 md:col-span-2">
                      <button
                        type="submit"
                        disabled={isSaving}
                        className="bg-slate-900 text-white rounded-sm h-10 px-4 text-sm font-medium disabled:opacity-60"
                        data-testid={`staff-save-${member.id}`}
                      >
                        {isSaving ? "Salvataggio..." : "Salva"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="border border-slate-200 rounded-sm h-10 px-4 text-sm font-medium"
                        data-testid={`staff-cancel-edit-${member.id}`}
                      >
                        Annulla
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
          {visibleStaff.length === 0 && (
            <div className="border border-dashed border-slate-200 rounded-sm p-4 text-sm text-slate-500">
              Nessun membro staff disponibile.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
